import { SelectDropdown } from '../shared/SelectDropdown';
import { useCallback, useState } from 'react';
import { useProject } from '../../hooks/useProjects';
import { useTeamMembers } from '../../hooks/useTeams';
import { ISSUE_TYPE_PICKER_OPTIONS } from '../../lib/issueTypeOptions';
import { HugeiconsIcon } from '@hugeicons/react';
import { StarIcon, CancelCircleIcon } from '@hugeicons/core-free-icons';
import { clsx } from 'clsx';
import type { Issue } from '@overlayqa/shared';
import { deriveIssueTitle, normalizeIssueTitleEdit } from '@overlayqa/shared';
import { CaptureHeading } from '@overlayqa/shared/ui';
import '../../../../shared/src/ui/captureHeading.css';
import { useCreateIssue, useUpdateIssue } from '../../hooks/useIssues';
import { useAuth } from '../../lib/auth';
import { useIssueForm } from './hooks/useIssueForm';
import { useIssueAiAnalysis } from './hooks/useIssueAiAnalysis';
import { ScreenshotSection, AiSuggestionCard } from './IssueDrawerParts';
import type { SelectionRect } from './SelectionOverlay';
import { log } from '@/lib/logger';

log.info('[IssueDrawer] Module loaded');

export interface IssueDraftFields {
  title: string; description: string; severity: Issue['severity']; type: Issue['type']; assigneeId?: string | null;
}

interface IssueDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  /** An explicit X/Cancel dismissal; successful saves still call onClose. */
  onDismiss?: () => void;
  projectId: string;
  figmaFileKey?: string;
  selection?: SelectionRect;
  issue?: Issue; // For editing existing issue
  onCreate?: (fields: IssueDraftFields) => Promise<void>;
  /** Captured element name used only when the reviewer leaves Title empty. */
  captureTitle?: string;
}

export function IssueDrawer({
  isOpen,
  onClose,
  onDismiss,
  projectId,
  figmaFileKey,
  selection,
  issue,
  onCreate,
  captureTitle,
}: IssueDrawerProps) {
  log.info('[IssueDrawer] Render', { isOpen, issueId: issue?.id });

  const { getToken } = useAuth();
  const createIssue = useCreateIssue();
  const updateIssue = useUpdateIssue();

  const form = useIssueForm({ isOpen, issue, selection });
  const projectQuery = useProject(isOpen ? projectId : '');
  const project = projectQuery.data;
  const members = useTeamMembers(isOpen ? project?.teamId : undefined);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [customPending, setCustomPending] = useState(false);
  const membersLoading = projectQuery.isLoading || members.isLoading;
  const membersError = projectQuery.error || members.error;
  const assigneeOptions = [
    ...(!issue ? [{ value: '__self__', label: 'Me' }] : []),
    { value: '', label: 'Unassigned' },
    ...(members.data ?? []).flatMap(member => member.user ? [{ value: member.user.id, label: member.user.name || member.user.email }] : []),
  ];

  const ai = useIssueAiAnalysis({
    description: form.description,
    isCreating: form.isCreating,
    isOpen,
    getToken,
  });

  const handleApplySuggestions = useCallback(() => {
    log.info('[IssueDrawer] handleApplySuggestions called');
    const result = ai.applySuggestions();
    if (result) {
      form.setTitle(result.title);
      if (result.description) {
        form.setDescription(result.description);
      }
      form.setSeverity(result.severity);
    }
  }, [ai, form]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    log.info('[IssueDrawer] handleSubmit — isCreating:', { isCreating: form.isCreating });

    const submittedTitle = normalizeIssueTitleEdit(form.title, issue?.title) || captureTitle || form.title.trim();
    if (!submittedTitle) return;

    setSaveError(null);
    try {
      if (onCreate && !issue) {
        setCustomPending(true);
        await onCreate({ title: submittedTitle, description: form.description, severity: form.severity, type: form.type, assigneeId: form.assigneeId });
      } else if (issue) {
        log.info('[IssueDrawer] Updating issue:', { id: issue.id });
        await updateIssue.mutateAsync({
          id: issue.id,
          data: {
            title: submittedTitle,
            description: form.description,
            severity: form.severity,
            type: form.type,
            assigneeId: form.assigneeId,
          },
        });
      } else {
        log.info('[IssueDrawer] Creating new issue in project:', { projectId });
        await createIssue.mutateAsync({
          projectId,
          title: submittedTitle,
          description: form.description || undefined,
          severity: form.severity,
          type: form.type,
          assigneeId: form.assigneeId,
          figmaFileKey,
          selectionX: selection?.x,
          selectionY: selection?.y,
          selectionWidth: selection?.width,
          selectionHeight: selection?.height,
        });
      }
      onClose();
    } catch (error) {
      log.error('[IssueDrawer] Failed to save issue:', { error });
      setSaveError('Your issue was not saved. Try again.');
    } finally {
      setCustomPending(false);
    }
  };

  const isPending = customPending || createIssue.isPending || updateIssue.isPending;
  const dismiss = () => {
    if (!isPending) (onDismiss ?? onClose)();
  };

  return (
    <>
      {/* Backdrop - no click to close, user must click Cancel */}
      <div
        className={clsx(
          'fixed inset-0 bg-black/20 z-40 transition-opacity',
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
      />

      {/* Drawer */}
      <div
        className={clsx(
          'fixed top-0 right-0 h-full w-[440px] max-w-full bg-white shadow-xl z-50 transition-transform',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        {/* Header */}
        <header className="flex items-center justify-between px-6 py-4 border-b border-gray-200 oq-capture-heading-header">
          <div className="flex items-center gap-2 oq-capture-heading-group">
            {form.isCreating && (
              <span className="text-primary-500">
                <HugeiconsIcon icon={StarIcon} size={20} strokeWidth={2} color="currentColor" className="w-5 h-5" />
              </span>
            )}
            {captureTitle ? <CaptureHeading as="h2" title={deriveIssueTitle({ title: normalizeIssueTitleEdit(form.title, issue?.title),
              elementData: { titleContext: { accessibleName: captureTitle } } })} /> :
              <h2 className="text-lg font-semibold text-gray-900">{issue ? 'Edit Issue' : 'Smart Issue Creator'}</h2>}
          </div>
          <button
            type="button"
            aria-label="Close issue"
            onClick={dismiss}
            disabled={isPending}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 oq-capture-heading-controls"
          >
            <HugeiconsIcon icon={CancelCircleIcon} size={20} strokeWidth={2} color="currentColor" className="w-5 h-5" />
          </button>
        </header>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5 overflow-y-auto h-[calc(100%-140px)]">
          {/* Screenshot preview */}
          {form.isCreating && (
            <ScreenshotSection
              screenshotUrl={form.screenshotUrl}
              onClearScreenshot={() => form.setScreenshotUrl(null)}
              selection={selection}
            />
          )}

          {/* Description (Primary Input for new issues) */}
          {form.isCreating && (
            <div>
              <label htmlFor="description" className="field-label">
                Description
              </label>
              <textarea
                id="description"
                value={form.description}
                onChange={(e) => form.setDescription(e.target.value)}
                placeholder="Describe the issue, feature, or task in detail..."
                className="input input-lg w-full min-h-[100px] resize-y border-2 border-primary-200 focus:border-primary-500"
                rows={4}
                autoFocus
              />
              {form.description.length > 0 && form.description.length < 10 && (
                <p className="field-hint mt-1">Type at least 10 characters for AI analysis</p>
              )}
            </div>
          )}

          {/* AI Analysis Section (only for new issues) */}
          {form.isCreating && (ai.suggestion || ai.isAnalyzing) && (
            <AiSuggestionCard
              suggestion={ai.suggestion}
              isAnalyzing={ai.isAnalyzing}
              onApply={handleApplySuggestions}
            />
          )}

          {/* AI Error */}
          {form.isCreating && ai.aiError && !ai.isAnalyzing && form.description.length >= 10 && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-500">
              {ai.aiError} - you can still create the issue manually.
            </div>
          )}

          {/* Title */}
          <div>
            <label htmlFor="title" className="field-label">
              Title {!captureTitle && <span className="text-red-500">*</span>}
            </label>
            <input
              id="title"
              type="text"
              value={form.title}
              onChange={(e) => form.setTitle(e.target.value)}
              placeholder={captureTitle || 'Enter issue title...'}
              className="input input-lg w-full"
              required={!captureTitle}
              autoFocus={!form.isCreating}
            />
          </div>

          {/* Description (for editing) */}
          {!form.isCreating && (
            <div>
              <label htmlFor="description-edit" className="field-label">
                Description
              </label>
              <textarea
                id="description-edit"
                value={form.description}
                onChange={(e) => form.setDescription(e.target.value)}
                placeholder="Add more details about the issue..."
                className="input input-lg w-full min-h-[100px] resize-y"
                rows={4}
              />
            </div>
          )}

          {/* Priority and Type */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="priority" className="field-label">
                Priority
              </label>
              <SelectDropdown
                id="priority"
                value={form.severity}
                onChange={(value) => form.setSeverity(value as typeof form.severity)}
                size="lg" className="w-full"
                options={[{ value: 'critical', label: 'Critical' }, { value: 'high', label: 'High' }, { value: 'medium', label: 'Medium' }, { value: 'low', label: 'Low' }]}
              />
            </div>
            <div>
              <label htmlFor="type" className="field-label">
                Type
              </label>
              <SelectDropdown
                id="type"
                value={form.type}
                onChange={(value) => form.setType(value as typeof form.type)}
                size="lg" className="w-full"
                options={ISSUE_TYPE_PICKER_OPTIONS}
              />
            </div>
          </div>
          <div>
            <label htmlFor="draft-assignee" className="field-label">Assignee</label>
            <SelectDropdown id="draft-assignee" aria-label="Assignee" value={form.assigneeId === undefined ? '__self__' : form.assigneeId ?? ''}
              options={assigneeOptions} onChange={value => form.setAssigneeId(value === '__self__' ? undefined : value || null)}
              disabled={membersLoading || !!membersError} size="lg" className="w-full" />
            {membersLoading && <p role="status" className="field-hint">Loading teammates...</p>}
            {membersError && <p role="alert" className="field-hint">Couldn’t load teammates. <button type="button" onClick={() => { if (projectQuery.error) void projectQuery.refetch(); else void members.refetch(); }}><span className="text-cap-trim block">Try again</span></button></p>}
          </div>
          {saveError && <p role="alert">{saveError}</p>}
        </form>

        {/* Footer */}
        <footer className="absolute bottom-0 left-0 right-0 px-6 py-4 border-t border-gray-200 bg-white">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={dismiss}
              className="btn-secondary flex-1"
              disabled={isPending}
            >
              <span className="text-cap-trim">Cancel</span>
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              className="btn-primary flex-1 flex items-center justify-center gap-2"
              disabled={isPending || (!form.title.trim() && !captureTitle)}
            >
              {form.isCreating && (
                <HugeiconsIcon icon={StarIcon} size={16} strokeWidth={2} color="currentColor" className="w-4 h-4" />
              )}
              <span className="text-cap-trim">{isPending ? 'Saving...' : issue ? 'Update issue' : 'Create issue'}</span>
            </button>
          </div>
        </footer>
      </div>
    </>
  );
}

